import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { User } from 'firebase/auth';

import {
  clearPinResetRequest,
  hasRecentAuth,
  reauthenticateWithGoogle,
  updatePin,
} from './pinReset';
import { pinAutoComplete, pinInputClassName, pinInputType } from './pinMask';
import { isFirestoreNotFound, isFirestorePermissionDenied } from './pinResetErrors';

const inputClass =
  'w-full rounded-[var(--radius-md)] border border-[var(--divider)] bg-[var(--bg-list)] px-4 py-3';
const primaryButtonClass =
  'pressable w-full rounded-[var(--radius-md)] bg-[var(--accent)] px-6 py-3 font-medium text-white disabled:opacity-40';

/**
 * PIN 재설정 (PLAN 5-12장).
 *
 * 1단계는 Google 재인증, 2단계는 새 PIN 입력이다. 모바일 리디렉션은 페이지를
 * 새로고침하므로, 화면이 다시 뜨면 `auth_time`을 확인해 곧바로 2단계로 건너뛴다.
 */
export function PinResetScreen({
  user,
  onReset,
  onCancel,
}: {
  user: User;
  onReset: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'verify' | 'newPin'>('verify');
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');

  // 리디렉션으로 돌아온 경우엔 이미 본인 확인이 끝나 있다
  useEffect(() => {
    let cancelled = false;
    clearPinResetRequest();

    void hasRecentAuth(user)
      .then((fresh) => {
        if (!cancelled && fresh) setStep('newPin');
      })
      .catch((err: unknown) => {
        // 오프라인이면 토큰을 새로 받을 수 없다 — 1단계에서 다시 시도하게 둔다
        console.error('[talk2nIcK] 최근 인증 확인 실패:', err);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  async function onVerify() {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const result = await reauthenticateWithGoogle(user);
      // 'redirected'면 페이지가 이동하므로 이 아래는 실행되지 않는다
      if (result === 'cancelled') return;
      if (result === 'verified') setStep('newPin');
    } catch (err) {
      console.error('[talk2nIcK] Google 재인증 실패:', err);
      setError(t('pinReset.failed'));
    } finally {
      setBusy(false);
    }
  }

  const pinValid = /^\d{6}$/.test(pin);
  const pinMatches = pin.length > 0 && pin === pinConfirm;

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!pinValid || !pinMatches || busy) return;
    setError(null);
    setBusy(true);
    try {
      await updatePin(user, pin);
      // 새 PIN으로 잠금을 풀면 LockGate가 앱 화면으로 넘어간다
      onReset();
    } catch (err) {
      console.error('[talk2nIcK] PIN 재설정 실패:', err);
      // 세 원인을 구분한다. not-found는 재시도로 고쳐지지 않으므로 다른 문구가 필요하다.
      setError(
        isFirestorePermissionDenied(err)
          ? t('error.rulesDenied')
          : isFirestoreNotFound(err)
            ? t('status.noProfile')
            : t('error.generic'),
      );
    } finally {
      setBusy(false);
    }
  }

  const pinError =
    pin.length > 0 && !pinValid
      ? t('onboarding.pinLength')
      : pinConfirm.length > 0 && !pinMatches
        ? t('onboarding.pinMismatch')
        : null;

  return (
    <div className="flex flex-1 min-h-0 flex-col items-center justify-center gap-4 overflow-y-auto max-h-[calc(var(--app-height,100dvh)_-_var(--kb-height,0px))] px-8 pt-[max(var(--safe-top),2rem)] pb-[max(var(--safe-bottom),2rem)] text-center [&>*]:my-auto">
      <div className="flex flex-col items-center gap-3">
        <div className="text-4xl">🔑</div>
        <h1 className="text-2xl font-semibold">{t('pinReset.title')}</h1>
        <p className="text-sm text-[var(--text-secondary)]">{t('pinReset.hint')}</p>
      </div>

      {step === 'verify' ? (
        <div className="w-full max-w-xs space-y-3">
          <button
            type="button"
            className={primaryButtonClass}
            onClick={() => void onVerify()}
            disabled={busy || checking}
          >
            {busy ? t('pinReset.verifying') : t('pinReset.verify')}
          </button>
          <button
            type="button"
            className="tap-target w-full text-xs text-[var(--text-secondary)]"
            onClick={onCancel}
            disabled={busy}
          >
            {t('common.back')}
          </button>
        </div>
      ) : (
        <form onSubmit={onSave} className="w-full max-w-xs space-y-3">
          <p className="text-sm text-[var(--text-secondary)]">{t('onboarding.pinHint')}</p>
          <input
            type={pinInputType}
            className={pinInputClassName(`${inputClass} text-center text-2xl tracking-widest`)}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder={t('onboarding.pinPlaceholder')}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={pinAutoComplete}
            maxLength={6}
            autoFocus
            disabled={busy}
          />
          <input
            type={pinInputType}
            className={pinInputClassName(`${inputClass} text-center text-2xl tracking-widest`)}
            value={pinConfirm}
            onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder={t('onboarding.pinConfirmPlaceholder')}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={pinAutoComplete}
            maxLength={6}
            disabled={busy}
          />
          <button
            type="submit"
            className={primaryButtonClass}
            disabled={!pinValid || !pinMatches || busy}
          >
            {busy ? t('onboarding.finishing') : t('pinReset.save')}
          </button>
          <button
            type="button"
            className="tap-target w-full text-xs text-[var(--text-secondary)]"
            onClick={onCancel}
            disabled={busy}
          >
            {t('common.back')}
          </button>
        </form>
      )}

      {(pinError ?? error) && <p className="text-xs text-[var(--danger)]">{pinError ?? error}</p>}
    </div>
  );
}
